<template>
  <div style="height: 100%;">
    <div class="messageContainer">
      <div class="messageHeader d-flex flex-row align-items-center">
        <div class="col-md-6">
          <img
            class="float-left"
            :src="require(`@/assets/image/${
              patient && patient.gender === 'male' ?
                'boy':
                'girl'
            }.svg`)"
            width="35"
            height="35"
            :alt="patient && patient.first_name"
            v-b-tooltip.hover
            :title="patient && patient.first_name"
          >

          <h5 class="mx-2 font-weight-bold mb-0 p-1">
            {{ patient.first_name | capitalizefirst }}
            {{ patient.last_name | capitalizefirst }}
          </h5>
        </div>
        <div class="col-md-4 text-right">
           <b-form-checkbox v-model="is_text_message" name="Text Message">Send as text message</b-form-checkbox>
        </div>
        <div class="col-md-2 text-right patient-video-call">
          <span>
            <button 
              type="button"
              class="btn fa fa-video-camera"
              variant="light"
              aria-hidden="true"
              style="outline: none;"
              @click="initiateVideoCall(patient.patient_id)"
            />
          </span>
        </div>
      </div>
      <div
        class="messages text-wrap d-flex flex-column-reverse slimscroll"
        ref="messages"
        id="messages"
        v-if="patientMessageKeys.length"
      >
        <template v-for="patientMessageKey in patientMessageKeys">
          <div
            v-for="patientMessage in groupedMessages[patientMessageKey]"
            :key="patientMessage.id"
            class="m-2 p-2 d-flex flex-column message"
          >
            <span class="info">
              {{ getFormattedDate(patientMessage.created_at, dateTimeFormat) }}
            </span>
            <span class="description">{{ patientMessage.description }}</span>
          </div>
          <div
            :key="patientMessageKey"
            class="kebabText"
          >
            <span>{{ patientMessageKey }}</span>
          </div>
        </template>
      </div>
      <div
        v-else
        class="messages"
      >
        <div class="m-auto d-flex flex-column align-items-center">
          <img
            :src="require(`@/assets/image/comment.svg`)"
            :alt="patient && patient.first_name"
            v-b-tooltip.hover
            :title="patient && patient.first_name"
            class="mb-2"
          >
          <span class="blankMessageText">Start by typing in the field below</span>
        </div>
      </div>
      <div class="messageBox">
        <b-input-group
          size="md"
          class="py-3 px-5"
        >
          <b-input-group-text
            slot="append"
            class="bg-white border-0 borderBottom"
          >
            <strong>
              <i class="icon ion-ios-send" />
            </strong>
          </b-input-group-text>
          <b-form-input
            class="border-0 borderBottom"
            placeholder="Type a new message"
            @keyup.enter.native="handleSendMessage"
            @blur.native="handleSendMessage"
          />
        </b-input-group>
      </div>
    </div>
    <b-modal 
      id="video-call"
      size="xl"
      centered
      no-close-on-backdrop
      no-close-on-esc
      title-sr-only
      hide-header
      hide-footer
      aria-label="Video Call"
      body-class="mh-100 p-0"
      @shown="onShown"
      @hidden="closeVideoChat">
      <template slot="default" slot-scope="{ ok }">
        <b-container fluid>
          <b-row>
            <b-col cols="9"
              class="pr-0 pl-0">
              <b-row id="videoConference" 
                class="h-100 w-100 mr-0 ml-0">
              </b-row>
            </b-col>
            <b-col class="pt-2 pb-2">
              <h5>Notes</h5>
              <b-form-textarea 
                aria-label="Notes of Patient"
                rows="17"
                v-model="description">
              </b-form-textarea>
              <b-button variant="danger" 
                class="float-right mt-2"
                @click="ok">
                Save Notes & End Call
              </b-button>
            </b-col>
          </b-row>
        </b-container>
      </template>
    </b-modal>
  </div>
</template>

<script>

import { mapGetters } from 'vuex';
import constants from '@/constants/app.properties';
import infiniteScroller from '@/utils/infiniteScroller';

export default {
  name: 'Message',
  components: {},
  props: {
    patient: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      dateTimeFormat: constants.DATE_TIME_FORMAT,
      filter: {
        pageNo: 1,
        limit: 20
      },
      description: '',
      isTitleSROnly: true,
      is_text_message: false,
      videoCallDetails: {}
    };
  },
  computed: {
    ...mapGetters({
      patientMessages: 'message/getPatientMessages',
      patientMessageKeys: 'message/getPatientMessageKeys',
      groupedMessages: 'message/getGroupedMessages',
      patientMessageTotalCount: 'message/getPatientMessageTotalCount'
    }),
    getUserName() {
      return this.$store.state.auth.user.first_name + ' ' + this.$store.state.auth.user.last_name;
    }
  },
  watch: {
    filter: {
      handler: 'fetchMessages',
      immediate: true
    },
    patient: {
      handler: 'handlePatientChange',
      immediate: true
    }
  },
  mounted() {
    const element = document.getElementById('messages');
    if (element) {
      element.addEventListener('scroll', event =>
        infiniteScroller(
          event,
          () => {
            this.filter = {
              ...this.filter,
              ...{ pageNo: this.filter.pageNo + 1 }
            };
          },
          null,
          {
            percentage: 0,
            currentLength: this.patientMessages.length,
            totalLength: this.patientMessageTotalCount,
            type: constants.SCROLL_TYPE_HISTORY
          }
        ));
    }
  },
  beforeDestroy() {
    const element = document.getElementById('messages');
    if (element) {
      element.removeEventListener('scroll', event => infiniteScroller(event));
    }
  },
  methods: {
    closeVideoChat() {
      this.$store.dispatch('message/closeVideoChat', {
        room_id: this.$store.state.message.videoCallDetails.resourceId
      }).then(() => {
        this.$store.state.message.videoCallDetails.vidyoConnector.Disconnect();
        this.$store.state.message.videoCallDetails.vidyoConnector.Destruct();
      });
      if (this.description) {
        this.$store.dispatch('message/saveVideoCallNotes', {
          description: this.description,
          patient_id: this.patient.patient_id,
          care_navigator_id: this.patient.care_navigator_id,
          video_call_id: this.$store.state.message.videoCallDetails.video_call_id
        }).then(() => {
          this.description = '';
          this.$store.state.message.videoCallDetails = undefined;
        });
      }
    },
    handleSendMessage(event) {
      if (event.target.value) {
        // eslint-disable-next-line camelcase
        const { patient_id, care_navigator_id } = this.patient;
        this.$store.dispatch('message/sendMessage', {
          description: event.target.value,
          is_text_message: this.is_text_message,
          patient_id,
          care_navigator_id
        });
        event.target.value = '';
      }
    },
    fetchMessages() {
      this.$store.dispatch('message/fetchPatientMessages', {
        id: this.patient.patient_id,
        filter: this.filter
      });
    },
    handlePatientChange() {
      const container = document.getElementById('messages');
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
      this.filter.pageNo = 1;
      this.fetchMessages();
    },
    initiateVideoCall(patientId) {
      this.$store.dispatch('message/getRoomId', {
        patientId,
      }).then((response) => {
        this.$store.state.message.videoCallDetails = response;
        this.$store.state.message.videoCallDetails.userName = this.getUserName;
        this.$bvModal.show("video-call");
      });
    },
    onShown() {
      this.$store.dispatch('message/startVideoCall');
    }
  }
};
</script>
<style lang="scss" scoped>
@import "./style.scss";
</style>

